import React, { useEffect } from 'react'
import Drawer from '../../../components/common/Drawer'
import { useForm } from 'react-hook-form'
import Input from '../../../components/form/input/InputField'
import Label from '../../../components/form/Label'
import { useBusiness } from '../../../context/BusinessContext'
import Checkbox from '../../../components/form/input/Checkbox'
import ColorPicker from '../../../components/common/ColorPicker'
import { api } from '../../../configs/api'
import toast from 'react-hot-toast'

interface PositionProps {
    position: 'before' | 'after'
    setPosition: (position: string) => void
}

const BusinessCreateStep = ({ open, handleClose }: { open: boolean; handleClose: () => void }) => {
    const { business, currentTab, currentStep, startLoading, stopLoading } = useBusiness()
    const [color, setColor] = React.useState<string>('#12B76A')
    const [totalSteps, setTotalSteps] = React.useState<number>(0)
    const [position, setPosition] = React.useState<PositionProps['position']>('after')
    const form = useForm({ mode: 'onChange' })
    const formRef = React.useRef<HTMLFormElement>(null)

    const saveDatabase = async (values: any) => {
        if (!values) return

        try {
            const formattedValues = {
                ...values,
                stepID: parseInt(values.stepID)
            }
            const response = await fetch(`${api}business/createStep`, {
                method: 'POST',
                body: JSON.stringify(formattedValues)
            })
            const result = await response.json()
            return result.stepID
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const saveContext = (pipelineID: string, stepID: string, name: string) => {
        try {
            const pipelineIndex = business.pipelines.findIndex((pipeline: any) => pipeline.id === pipelineID)
            if (pipelineIndex === -1) {
                return false
            }

            const steps = business.pipelines[pipelineIndex].steps
            let insertIndex = 0 // Posição inicial padrão

            if (currentStep) {
                // Se a nova fase for baseada em uma existente
                const stepIndex = steps.findIndex((step: any) => step.id == parseInt(currentStep.id))
                if (stepIndex === -1) {
                    return false
                }

                insertIndex = position === 'before' ? stepIndex : stepIndex + 1
            } else {
                // Se for uma fase avulsa
                insertIndex = position === 'before' ? 0 : steps.length
            }

            // Inserir nova etapa na posição correta
            steps.splice(insertIndex, 0, {
                id: stepID,
                title: name,
                color,
                business: []
            })

            setTotalSteps(totalSteps + 1)

            return true
        } catch (error) {
            console.log(error)
            return false
        }
    }

    const handleSave = async (data: any) => {
        const pipelineID = business.pipelines.find((pipeline: any) => pipeline.value === currentTab)?.id
        if (!pipelineID) return

        const values = {
            ...data,
            color,
            position,
            stepID: currentStep?.id || null,
            pipelineID: Number(pipelineID)
        }

        try {
            startLoading()

            //* Insere no BD
            const stepID = await saveDatabase(values)
            if (!stepID) {
                toast.error('Não foi possível criar a fase!')
                return
            }

            //? Insere no contexto (business: pipeline => step)
            const result = saveContext(pipelineID, stepID, data.name)
            result ? toast.success('Fase criada com sucesso!') : toast.error('Não foi possível criar a fase!')
        } catch (error) {
        } finally {
            stopLoading()
            handleClose()
        }
    }

    useEffect(() => {
        if (business) {
            const total = business.pipelines.find((pipeline: any) => pipeline.value === currentTab)?.steps.length
            setTotalSteps(total || 0)
        }
    }, [business, currentTab])

    useEffect(() => {
        form.setValue('name', '')
    }, [open])

    return (
        <Drawer
            open={open}
            handleClose={handleClose}
            title='Nova fase'
            handleSave={() => formRef.current?.requestSubmit()}
            maxWidth='max-w-xl'
        >
            <form ref={formRef} onSubmit={form.handleSubmit(handleSave)}>
                <div className='space-y-3'>
                    <div>
                        <Label required>Nome da fase</Label>
                        <Input {...form.register('name', { required: true })} placeholder='Nome da fase' />
                    </div>
                    <div>
                        <Label>Cor</Label>
                        <ColorPicker color={color} onChange={setColor} />
                    </div>

                    {/* Nova fase (tabs pipeline): Se possui pelo menos uma fase, pergunta se é a primeira ou última fase, se ainda não existe, não exibe posição */}
                    {/* Nova fase (selecionada de uma step existente): Pergunta se quer criar antes ou depois da existente */}
                    {totalSteps > 0 && (
                        <div>
                            <Label>Posição</Label>
                            <div className='space-y-4'>
                                {currentStep ? (
                                    <>
                                        <Checkbox
                                            label={
                                                <p>
                                                    Antes de{' '}
                                                    <strong className='text-brand-500'>{currentStep?.title}</strong>
                                                </p>
                                            }
                                            checked={position === 'before'}
                                            onChange={() => {
                                                setPosition('before')
                                            }}
                                            size='large'
                                            color='primary'
                                        />
                                        <Checkbox
                                            label={
                                                <p>
                                                    Depois de{' '}
                                                    <strong className='text-brand-500'>{currentStep?.title}</strong>
                                                </p>
                                            }
                                            checked={position === 'after'}
                                            onChange={() => setPosition('after')}
                                            size='large'
                                            color='primary'
                                        />
                                    </>
                                ) : totalSteps > 0 ? (
                                    <>
                                        <Checkbox
                                            label='Primeira fase'
                                            checked={position === 'before'}
                                            onChange={() => setPosition('before')}
                                            size='large'
                                            color='primary'
                                        />
                                        <Checkbox
                                            label='Última fase'
                                            checked={position === 'after'}
                                            onChange={() => setPosition('after')}
                                            size='large'
                                            color='primary'
                                        />
                                    </>
                                ) : null}
                            </div>
                        </div>
                    )}
                </div>
            </form>
        </Drawer>
    )
}

export default BusinessCreateStep
